import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { ReferenceDataCategoriesConstants, ReferenceDataOptionLists, ReferenceDataService } from 'rome-library';
import { UserService } from '../../core/services/user.service';
import { MortgageAccountDetailsDto } from '../../interfaceTypes/direct/customer-hub/mortgage-hub/account-overview/models/mortgage-account-details-dto';
import { TagManagerConstants } from '../constants/tag-manager-constants';
import { DirectTaggingViewModel } from '../models/direct-tagging-viewmodel.model';
import { TaggingService } from './tagging.service';

@Injectable({
  providedIn: 'root'
})
export class DirectTaggingService extends TaggingService {
  private readonly viewmodel = new DirectTaggingViewModel();

  constructor(
    referenceDataService: ReferenceDataService,
    gtmService: GoogleTagManagerService,
    private readonly router: Router,
    private readonly userService: UserService
  ) {
    super(referenceDataService, gtmService);

    this.viewmodel.PageNameParam = this.getConfigValue(TagManagerConstants.PageNameParam);
    this.viewmodel.PageNameParamValuePartial = this.getConfigValue(TagManagerConstants.PageNameParamValuePartial);
    this.viewmodel.ProductNameParam = this.getConfigValue(TagManagerConstants.ProductNameParam);
    this.viewmodel.ProductNameParamValue = this.getConfigValue(TagManagerConstants.ProductNameParamValue);
    this.viewmodel.UserLoginStatusParam = this.getConfigValue(TagManagerConstants.UserLoginStatusParam);
    this.viewmodel.ProductVariationParam = this.getConfigValue(TagManagerConstants.ProductVariationParam);
    this.viewmodel.ProductVariationParamValueResidental = this.getConfigValue(
      TagManagerConstants.ProductVariationParamValueResidental
    );
    this.viewmodel.ProductVariationParamValueBtl = this.getConfigValue(
      TagManagerConstants.ProductVariationParamValueBtl
    );
    this.viewmodel.FormNameParam = this.getConfigValue(TagManagerConstants.FormNameParam);
    this.viewmodel.QuoteIdParam = this.getConfigValue(TagManagerConstants.QuoteIdParam);
    this.viewmodel.TransactionIdParam = this.getConfigValue(TagManagerConstants.TransactionIdParam);
    this.viewmodel.AccountIdParam = this.getConfigValue(TagManagerConstants.AccountIdParam);
    this.viewmodel.AppStageParam = this.getConfigValue(TagManagerConstants.AppStageParam);
    this.viewmodel.AppStageParamValueAip = this.getConfigValue(TagManagerConstants.AppStageParamValueAip);
    this.viewmodel.AppStageParamValueApp = this.getConfigValue(TagManagerConstants.AppStageParamValueApp);
    this.viewmodel.DecisionParam = this.getConfigValue(TagManagerConstants.DecisionParam);
    this.viewmodel.DecisionParamValueAccept = this.getConfigValue(TagManagerConstants.DecisionParamValueAccept);
    this.viewmodel.DecisionParamValueDecline = this.getConfigValue(TagManagerConstants.DecisionParamValueDecline);
    this.viewmodel.DecisionParamValueRefer = this.getConfigValue(TagManagerConstants.DecisionParamValueRefer);
    this.viewmodel.GoogleVirtualPageViewEventName = this.getConfigValue(TagManagerConstants.GoogleEventName);
    this.viewmodel.setCaseStatusMap();
  }

  protected getConfigValue(name: string): string {
    return this.referenceDataService.getConfigurationValueByCode(
      ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
      TagManagerConstants.TagManagerConfigName,
      name
    );
  }

  public sendDataLayer(): void {
    if (!this.isTagManEnabled) {
      return;
    }

    const currentStateName = this.router.url.split('/').reverse()[0].toLowerCase();

    const tagManagerParam = {};
    tagManagerParam[this.viewmodel.PageNameParam] = this.getPageName(currentStateName);
    tagManagerParam[this.viewmodel.ProductNameParam] = this.viewmodel.ProductNameParamValue;
    tagManagerParam[this.viewmodel.UserLoginStatusParam] = this.userService.getUser()?.IsAuthenticated;
    tagManagerParam[this.viewmodel.FormNameParam] = currentStateName;
    tagManagerParam[TagManagerConstants.EventParamName] = this.viewmodel.GoogleVirtualPageViewEventName;

    if (this.dataLayer.isResidential) {
      tagManagerParam[this.viewmodel.ProductVariationParam] = this.dataLayer.isResidential
        ? this.viewmodel.ProductVariationParamValueResidental
        : this.viewmodel.ProductVariationParamValueBtl;
    }

    if (this.dataLayer.quoteId) {
      tagManagerParam[this.viewmodel.QuoteIdParam] = this.dataLayer.quoteId.toString();
    }

    if (this.dataLayer.transactionId) {
      tagManagerParam[this.viewmodel.TransactionIdParam] = this.dataLayer.transactionId.toString();
    }

    if (this.dataLayer.accountId) {
      tagManagerParam[this.viewmodel.AccountIdParam] = this.dataLayer.accountId.toString();
    }

    if (this.dataLayer.appStage) {
      tagManagerParam[this.viewmodel.AppStageParam] = this.dataLayer.appStage;
      this.dataLayer.appStage = undefined;
    }

    if (this.dataLayer.decision) {
      tagManagerParam[this.viewmodel.DecisionParam] = this.dataLayer.decision;
      this.dataLayer.decision = undefined;
    }

    void this.gtmService.pushTag(tagManagerParam);
  }

  private getPageName(state: string): string {
    return ReferenceDataService.formatStringWithArguments(this.viewmodel.PageNameParamValuePartial, [state]);
  }

  public setMortgageType(mortgageType): void {
    if (this.isTagManEnabled) {
      this.dataLayer.isResidential = mortgageType === ReferenceDataOptionLists.LoanGroupTypeResidential;
    }
  }

  public setSubmissionStatus(caseStatusCode: string): void {
    if (this.isTagManEnabled) {
      const submissionStatus = this.viewmodel.getSubmissionStatus(caseStatusCode);

      if (submissionStatus) {
        this.dataLayer.appStage = submissionStatus.appStage;
        this.dataLayer.decision = submissionStatus.decision;
      }
    }
  }

  public setTransactionId(): void {
    if (this.isTagManEnabled) {
      this.dataLayer.transactionId = this.dataLayer.quoteId;
    }
  }

  public setQuoteId(caseNumber: string): void {
    if (this.isTagManEnabled) {
      this.dataLayer.quoteId = caseNumber;
    }
  }

  public setAccountId(samId: string): void {
    if (this.isTagManEnabled) {
      this.dataLayer.accountId = samId;
    }
  }

  public sendMortgageAccountDataLayer(caseNumber: number, mortgageAccountDetails: MortgageAccountDetailsDto): void {
    if (!this.isTagManEnabled) {
      return;
    }

    const tagManagerParam = {};
    tagManagerParam[TagManagerConstants.CaseNumberParamName] = caseNumber;
    tagManagerParam[TagManagerConstants.MortgageClassParamName] = mortgageAccountDetails.MortgageAccount.MortgageClass;
    tagManagerParam[TagManagerConstants.AccountTypeParamName] = mortgageAccountDetails.MortgageAccount.AccountType;
    tagManagerParam[TagManagerConstants.HeaderSwitchEligibilityParamName] =
      !mortgageAccountDetails.IsSwitchInProgress && mortgageAccountDetails.IsSwitchable;
    tagManagerParam[TagManagerConstants.TransactionInProgressParamName] =
      mortgageAccountDetails.TransactionInProgressCode !== null
        ? mortgageAccountDetails.TransactionInProgressCode
        : false;
    tagManagerParam[TagManagerConstants.RomeUserIdParamName] = mortgageAccountDetails.DirectCustomerID;
    tagManagerParam[TagManagerConstants.EventParamName] = TagManagerConstants.EventCustomerAccountLoaded;

    void this.gtmService.pushTag(tagManagerParam);
  }
}
